import React, { createContext, useContext, useEffect, useState } from "react";
import {
  getCurrentUser,
  signOut,
  signInAnonymously as signInAnonymouslyAuth,
} from "./auth";
import { configureAWS } from "./config";

// Initialize AWS Amplify with the configuration
configureAWS();

// Define our auth context state type
interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: {
    userId: string; // This will be the Cognito sub claim
    email?: string; // User's email if available
    username?: string; // Cognito username
    displayName?: string; // User's display name
    idToken?: string; // Raw ID token for API requests
    isAnonymous?: boolean; // Flag for anonymous users
    tokens?: any; // Full token object
    isAuthenticated: boolean;
  } | null;
  logout: () => Promise<void>;
  signInAnonymously: () => Promise<any>;
  refreshUser: () => Promise<void>; // Added function to manually refresh user state
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  logout: async () => {}, // Empty function as placeholder
  signInAnonymously: async () => null, // Empty function as placeholder
  refreshUser: async () => {}, // Empty function as placeholder for the refresh
});

/**
 * AuthProvider component to wrap your application with
 * This provides authentication state and methods to all child components
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Function to check if user is authenticated
  const checkUser = async () => {
    try {
      const userData = await getCurrentUser();
      console.log("User data:", userData);
      if (userData) {
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error("Error checking user:", error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Check for authenticated user on component mount and set up refresh interval
  useEffect(() => {
    // Initial check
    checkUser();

    // Set up periodic refresh of authentication state
    const authRefreshInterval = setInterval(() => {
      checkUser();
    }, 30000); // Check every 30 seconds

    // Clean up on unmount
    return () => {
      clearInterval(authRefreshInterval);
    };
  }, []);

  // Logout function
  const logout = async () => {
    try {
      await signOut();
      setUser(null);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Anonymous sign in function
  const signInAnonymously = async () => {
    try {
      const anonUser = await signInAnonymouslyAuth();
      if (anonUser) {
        setUser(anonUser);
        return anonUser;
      }
      return null;
    } catch (error) {
      console.error("Error signing in anonymously:", error);
      return null;
    }
  };

  // Function to manually refresh the user state
  const refreshUser = async () => {
    console.log("Manual refresh of user state requested");
    await checkUser();
  };

  // The value to be provided to consumers of this context
  const value = {
    isAuthenticated: !!user,
    isLoading,
    user,
    logout,
    signInAnonymously,
    refreshUser, // Expose the refresh function to consumers
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use the auth context
 * Usage: const { isAuthenticated, user, logout } = useAuth();
 */
export const useAuth = () => useContext(AuthContext);

export default AuthProvider;
